/*
 * The cover flight (ui-ux.md §4 "Cover flight"): where a poster was on screen
 * when it was activated, so the title page's hero case can fly from exactly
 * there. `Poster` records on pointer-down / Enter; `HeroCover` takes it once.
 * One pending flight per title id, valid only while the click's page
 * transition is still running: a hero that mounts later (the title wasn't
 * cached, so a skeleton showed first) must not fly from where the old page's
 * cover used to be — a small poster growing out of nowhere read as a resize.
 * Those arrivals take the case swing instead.
 */

export interface FlightOrigin {
  left: number;
  top: number;
  width: number;
  /** The poster image as drawn (`currentSrc`), when it had one. */
  src?: string;
  /** The title's palette — the poster's placeholder, a fallback for `src`. */
  palette?: [string, string];
}

/** A cached title mounts within the page transition; anything later skips the flight. */
const MAX_AGE_MS = 400;

const pending = new Map<string, FlightOrigin & { at: number }>();

export function recordFlight(id: string, el: Element, palette?: [string, string]): void {
  const r = el.getBoundingClientRect();
  if (r.width === 0) return;
  pending.clear(); // only the latest activation can be the one navigating
  const src = el.querySelector?.('img')?.currentSrc || undefined;
  pending.set(id, { left: r.left, top: r.top, width: r.width, src, palette, at: performance.now() });
}

/** The origin for `id`, consumed: a second call (or a stale record) returns null. */
export function takeFlight(id: string): FlightOrigin | null {
  const f = pending.get(id);
  pending.delete(id);
  if (!f || performance.now() - f.at > MAX_AGE_MS) return null;
  return { left: f.left, top: f.top, width: f.width, src: f.src, palette: f.palette };
}
